<template>
	<div id="app" v-cloak>
		<h1 class="h1">三师课程课后AI服务平台</h1>
		<header-component v-if="$store.state.generalComponentState.isShowHeader"></header-component>
		<div class="mainOverflowY">
			<div class="main" :style="$store.state.generalComponentState.isSetPadding ? 'padding-bottom: 30px' : ''">
				<keep-alive>
					<router-view v-if="$route.meta.keepAlive"></router-view>
				</keep-alive>
				<router-view v-if="!$route.meta.keepAlive"></router-view>
			</div>
			<footer-component v-if="$store.state.generalComponentState.isShowFooter"></footer-component>
		</div>

		<GlobalTooltip :show="tooltipVisible" :page="page" :courseid="courseid" :mouse-x="mouseX" :mouse-y="mouseY"
			@lock-tooltip="lockTooltip" @release-tooltip="releaseTooltip" @icon-click="handleIconClick" />

		<book-box ref="bookBox" :parentObj="this"></book-box>

		<KnowledgeVisualization ref="knowledgeVisualization" />

		<floatMenu ref="floatMenu" v-show="isShowFloatMenu"></floatMenu>

		<!-- Global Scrolling Screenshot Floating Button -->
		<div v-if="$store.state.screenshotModeActive" class="screenshot-float-btn">
			<el-popover placement="left" width="180" trigger="hover" popper-class="screenshot-popover">
				<div class="screenshot-menu">
					<div class="menu-item" @click="captureFullPage">
						<i class="el-icon-bottom"></i> 网页滚动长截图
					</div>
					<div class="menu-item" @click="captureViewport">
						<i class="el-icon-copy-document"></i> 截取当前可视网页
					</div>
					<div class="menu-item" @click="captureNativeWindow">
						<i class="el-icon-monitor"></i> 截取其他应用窗口
					</div>
					<div class="menu-item text-danger" @click="closeScreenshotMode"
						style="border-top: 1px solid #ebeef5; margin-top: 5px; padding-top: 10px; color: #f56c6c;">
						<i class="el-icon-circle-close"></i> 退出全局截屏模式
					</div>
				</div>
				<div class="btn-inner" slot="reference">
					<i class="el-icon-camera-solid"></i>
					<span>截屏工具</span>
				</div>
			</el-popover>
		</div>
	</div>
</template>

<script>
import globalTooltip from '@/components/globalTooltip.vue';
import tooltipMixin from '@/mixins/tooltipMixin';
import bookBox from "@/components/bookBox.vue";
import KnowledgeVisualization from "@/components/knowledgeVisualization.vue";
import floatMenu from "@/components/floatMenu.vue";
import html2canvas from 'html2canvas';

export default {
	name: 'App',
	components: {
		globalTooltip,
		bookBox,
		floatMenu,
		KnowledgeVisualization
	},
	data() {
		return {
			isShowFloatMenu: true
		}
	},
	created() {
		const host = window.location.hostname; // 当前域名
		const ua = navigator.userAgent.toLowerCase();
		const isMobile = /iphone|android|ipad|ipod|mobile/i.test(ua);
		if (host === "school.wenms.com" && isMobile) {
			window.location.href = "https://schoolh5.wenms.com/";
		}
		if (host === "wmsschool.app.mshclass.com" && isMobile) {
			window.location.href = "http://devschoolh5.wenms.com/";
		}
	},
	watch: {
		$route: {
			immediate: true,
			handler(to) {
				if (to.path === '/student/course/videoList') {
					// 👇 移除样式
					document.documentElement.style.minWidth = 'auto';
				} else {
					// 👇 添加样式
					document.documentElement.style.minWidth = '1200px';
				}

				this.isShowFloatMenu = to.path !== '/';
			}
		}
	},
	mixins: [tooltipMixin],
	methods: {
		openBook(data) {
			if (this.$refs.bookBox) {
				this.$nextTick(() => {
					this.$refs.bookBox.openBox(data)
					if (this.$refs.floatMenu && this.$refs.floatMenu.$refs.chatBox) {
						this.$refs.floatMenu.$refs.chatBox.close()
					} else if (this.$refs.chatBox) {
						this.$refs.chatBox.close()
					}
					if (this.$refs.knowledgeVisualization) {
						this.$refs.knowledgeVisualization.visible = false;
					}
				})
			}
		},
		openChat(data) {
			if (this.$refs.floatMenu) {
				this.$nextTick(() => {
					this.$refs.floatMenu.$refs.chatBox.openChat(data)
					if (this.$refs.bookBox) this.$refs.bookBox.close()
					if (this.$refs.knowledgeVisualization) {
						this.$refs.knowledgeVisualization.visible = false;
					}
				})
			}
		},
		openVisualization(kp) {
			if (this.$refs.knowledgeVisualization) {
				this.$refs.knowledgeVisualization.open(kp);
				if (this.$refs.bookBox) {
					this.$refs.bookBox.close();
				}
				if (this.$refs.floatMenu && this.$refs.floatMenu.$refs.chatBox) {
					this.$refs.floatMenu.$refs.chatBox.close();
				} else if (this.$refs.chatBox) {
					this.$refs.chatBox.close();
				}
			}
		},
		handleIconClick(iconType) {
			if (this.currentElement) {
				const data = this.currentElement.dataset;
				if (iconType === 'book') {
					this.openBook(data)
				} else if (iconType === 'favorite') {
					this.openChat(data.content)
				} else if (iconType === 'visual') {
					this.openVisualization(data.content)
				}
			}
		},
		async captureFullPage() {
			const notifyObj = this.$notify.info({
				title: '自动滚动截屏发起',
				message: '系统正在向下滚动以加载完整内容，请勿操作页面...',
				duration: 0,
				position: 'bottom-right'
			});

			try {
				const element = document.querySelector('.mainOverflowY');
				if (!element) throw new Error('未找到滚动容器');

				// Scroll down to ensure lazy loaded images are triggered and let user SEE it
				const originalScrollTop = element.scrollTop;
				element.scrollTop = 0;
				let currentScroll = 0;
				while (currentScroll < element.scrollHeight - element.clientHeight) {
					currentScroll += 600;
					element.scrollTo({ top: currentScroll, behavior: 'smooth' });
					await new Promise(r => setTimeout(r, 400));
				}
				element.scrollTo({ top: 0, behavior: 'instant' });
				await new Promise(r => setTimeout(r, 200));

				notifyObj.message = '内容加载完成，正在合成长图中(可能微卡顿)...';

				const originalHeight = element.style.height;
				const originalOverflow = element.style.overflow;
				element.style.height = 'max-content';
				element.style.overflow = 'visible';

				const html2canvas = require('html2canvas');
				const canvas = await html2canvas(element, {
					useCORS: true,
					scale: 2,
					logging: false,
					scrollY: -window.scrollY,
					onclone: (clonedDoc) => {
						const btn = clonedDoc.querySelector('.screenshot-float-btn');
						if (btn) btn.style.display = 'none';
					}
				});

				element.style.height = originalHeight;
				element.style.overflow = originalOverflow;
				element.scrollTop = originalScrollTop;

				const link = document.createElement('a');
				link.href = canvas.toDataURL('image/png');
				link.download = `滚动截屏_${new Date().getTime()}.png`;
				link.click();

				notifyObj.close();
				this.$notify.success({
					title: '截屏成功',
					message: '高清长图已下载到您的电脑！',
					position: 'bottom-right'
				});
			} catch (err) {
				console.error('Capture failed:', err);
				if (notifyObj) notifyObj.close();
				this.$message.error('截屏失败，请稍后重试');
			}
		},
		async captureViewport() {
			const { Loading } = require('element-ui');
			const loadingInstance = Loading.service({
				lock: true,
				text: '正在截取可视区域...',
				spinner: 'el-icon-loading',
				background: 'rgba(0,0,0,0.7)'
			});

			try {
				const element = document.querySelector('.mainOverflowY');
				if (!element) throw new Error('未找到内容容器');

				const canvas = await html2canvas(element, {
					useCORS: true,
					scale: 2,
					logging: false,
					onclone: (clonedDoc) => {
						const btn = clonedDoc.querySelector('.screenshot-float-btn');
						if (btn) btn.style.display = 'none';
					}
				});

				const link = document.createElement('a');
				link.href = canvas.toDataURL('image/png');
				link.download = `视口截屏_${new Date().getTime()}.png`;
				link.click();
				this.$message.success('本页可视区域截取成功！');
			} catch (err) {
				console.error(err);
				this.$message.error('截取失败');
			} finally {
				loadingInstance.close();
			}
		},
		async captureNativeWindow() {
			try {
				// 唤起原生分享弹口，实现“选择当前窗口/跨屏录像”
				const stream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: false });
				const video = document.createElement('video');
				video.srcObject = stream;

				await new Promise((resolve) => {
					video.onloadedmetadata = () => {
						video.play();
						resolve();
					};
				});

				// 稍等 100ms 等待图像清晰
				await new Promise(r => setTimeout(r, 100));

				// 直接捕捉抽帧
				const canvas = document.createElement('canvas');
				canvas.width = video.videoWidth;
				canvas.height = video.videoHeight;
				const ctx = canvas.getContext('2d');
				ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

				// 立马停止所有推流，以免看到"正在共享屏幕"的录制栏停留
				stream.getTracks().forEach(track => track.stop());

				const link = document.createElement('a');
				link.href = canvas.toDataURL('image/png');
				link.download = `自由窗口截图_${new Date().getTime()}.png`;
				link.click();
				this.$message.success('已存留选中视窗快照！');
			} catch (err) {
				console.error(err);
				this.$message.info('已取消窗口选取');
			}
		},
		closeScreenshotMode() {
			this.$store.commit('setStateBasic', {
				paramName: 'screenshotModeActive',
				val: false
			});
		}
	}
}
</script>

<style lang="scss">
@import "@/assets/index.scss";
@import "//at.alicdn.com/t/c/font_4861229_l3m69040la.css";
@import "//at.alicdn.com/t/c/font_4798240_r8ek3yy9hza.css"; //之前

.download-filter {
	display: flex;
	align-items: center;
	column-gap: 15px;
	margin-bottom: 20px;
}

@font-face {
	font-family: "鸿雷拙书简体";
	src: url('https://file.wenms.com/Production/SchoolService/WebFile/score.ttf') format('truetype');
	font-weight: normal;
	font-style: normal;
	font-display: swap;
}

@keyframes elasticScale {
	0% {
		transform: scale(1);
		/* 初始状态 */
	}

	50% {
		transform: scale(1.2);
		/* 中间状态：放大到 1.3 倍 */
	}

	100% {
		transform: scale(1);
		/* 最终状态：放大到 1.1 倍 */
	}
}

@keyframes jumpOut {
	0% {
		transform: translateX(100%) scale(0);
		/* 初始状态：从下方进入，缩小 */
		opacity: 0;
		/* 初始透明度为 0 */
		box-shadow: 0 0 0 rgba(0, 0, 0, 0);
		/* 无阴影 */
	}

	50% {
		transform: translateX(-10px) scale(1.2);
		/* 中间状态：向上跳起，稍微放大 */
		opacity: 1;
		/* 完全可见 */
		box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
		/* 添加阴影 */
	}

	100% {
		transform: translateY(0) scale(1);
		/* 最终状态：回到原位，恢复正常大小 */
		box-shadow: 0 5px 10px rgba(0, 0, 0, 0.1);
		/* 阴影减弱 */
	}
}

.h1 {
	position: absolute;
	left: 0;
	top: -9999px;
}

.mainOverflowY {
	height: 100vh;
	overflow-y: auto;
	overflow-x: auto;

	&::-webkit-scrollbar {
		width: 6px;
		height: 6px;
	}

	&::-webkit-scrollbar-thumb {
		background: #DBDBDB;
		border-radius: 10px;
	}
}

.main {
	min-height: calc(100vh - 240px);
}

[v-cloak] {
	display: none;
}

.el-dialog {
	border-radius: 8px !important;
}

.el-pagination {
	text-align: right;
	padding: 0 !important;
}

.el-pagination.is-background .el-pager li:not(.disabled).active {
	background: #0D68FF !important;
}

.el-pager .active:hover {
	color: #fff !important;
}

.el-pager .active {
	color: #fff !important;
}

.el-pagination.is-background .el-pager li:not(.disabled):hover {
	color: #5192FF;
}

.el-pager li {
	background: #fff !important;
	font-size: 12px !important;
	color: #57585C !important;

	&:hover {
		background: #0D68FF !important;
		color: #fff !important;
	}
}

.btn-prev {
	background: #fff !important;
}

.btn-next {
	margin: 0 0 0 5px !important;
	background: #fff !important;
}

.el-button {
	transition: background 0.3s !important;
}

// .el-button--primary {
// 	background: #0D68FF !important;
// 	border: 0 !important;

// 	&:hover {
// 		background: #1b8bff !important;
// 	}
// }
.el-button--primary {
	background: #0D68FF !important;
	border-color: #0D68FF !important;
}

.el-button--primary.is-disabled {
	background-color: #a0cfff !important;
	border-color: #a0cfff !important;
}

.el-input__inner {
	letter-spacing: normal;
}

// .el-tooltip__popper {
// 	box-shadow: 0px 3px 8px 0px rgba(0, 0, 0, 0.1);
// }

// .el-tooltip__popper.is-dark {
// 	background: #fff !important;
// 	color: #666 !important;
// }

// .el-tooltip__popper.is-light {
// 	background: rgba(0, 0, 0, 0.65) !important;
// 	color: #FFFFFF !important;
// 	border: 0 !important;
// }

// .el-tooltip__popper[x-placement^=top] .popper__arrow:after {
// 	border-top-color: #fff !important;
// }

// .el-tooltip__popper[x-placement^="top"] .popper__arrow {
// 	border-top-color: #fff !important;
// }

.el-button--default:hover {
	background: 0 !important;
	border: 1px solid #DCDFE6 !important;
	color: #1b8bff !important;
}

.vc-photoshop {
	width: 450px !important;
}

.vc-ps-saturation-wrap {
	height: 220px !important;
}

.vc-ps-hue-wrap {
	height: 220px !important;
}

.vc-ps-ac-btn {
	margin-bottom: 5px !important;
}

.vc-ps-fields {
	padding-bottom: 0 !important;
}

.vc-ps-fields .vc-input__input {
	height: 18px !important;
	margin-bottom: 2px !important;
}

// .el-button--default {
// 	background: 0 !important;
// 	border: 1px solid #DCDFE6 !important;
// 	color: #606266 !important;
// }

.el-image-viewer__canvas img {
	background: #fff;
}

.fly-icon {
	width: 24px;
	height: 24px;
	border-radius: 50%;
	background: #0D68FF;
	text-align: center;
	line-height: 22px;
	z-index: 1000;
	padding: 3px;

	&::before {
		font-family: 'iconfont';
		content: '\e9f8';
		font-size: 16px;
		color: #FFF;
	}
}

.el-checkbox-group {
	font-size: 14px !important
}

.el-radio__input.is-checked .el-radio__inner {
	border-color: #0D68FF !important;
	background: #0D68FF !important;
}

.el-radio__input.is-checked+.el-radio__label {
	color: #0D68FF !important;
}

.el-checkbox__input.is-checked+.el-checkbox__label {
	color: #0D68FF !important;
}

.el-checkbox__input.is-checked .el-checkbox__inner {
	background-color: #0D68FF !important;
	border-color: #0D68FF !important;
}

.plainButton {
	border: 1px solid #0D68FF !important;
	color: #0D68FF !important;
	transition: background 0.1s !important;
	box-sizing: border-box;
	padding: 10px 19px;

	&:hover {
		background: #0D68FF !important;
		color: #fff !important;
		border: 1px solid #0D68FF !important;
	}
}

.defaultButton {
	background: #0D68FF !important;
	border: 1px solid #0D68FF !important;

	&:hover {
		background: #1b8bff !important;
		border: 1px solid #1b8bff !important;
	}

	i {
		margin-right: 5px !important;

		&~span {
			margin: 0 !important;
		}
	}
}

.el-button--text {
	font-size: 14px !important;
}

.my-centered-dialog {
	top: 50% !important;
	transform: translateY(-50%) !important;
	margin: 0 auto !important;
}

.el-calendar-table tr:nth-child(6) td:first-child {
	border-radius: 0 0 0 8px;
}

.el-calendar-table tr:nth-child(6) td:last-child {
	border-radius: 0 0 8px 0;
}

.el-dialog__body {
	border: 0 !important;
}

.el-dialog__header {
	border-bottom: 1px solid #EBEEF6;
}

.el-calendar-table thead th {
	color: #000000 !important;
	font-weight: bold !important;
}

.el-table--border {
	border-radius: 8px;
}

.message-box-body p {
	white-space: pre-line;
}

.publicOutermost {
	margin: 76px 5vw 0;
	position: relative;
}

::v-deep .MathJax .mjx-c2192 {
	width: 1.7em !important;
}

::v-deep mjx-n {
	font-size: 70.5% !important;
}

.el-color-dropdown {
	.el-button--default {
		display: none;
	}

	.el-color-dropdown__btns {
		text-align: left;
	}

	.el-button {
		margin-left: 10px;
	}
}

.screenshot-float-btn {
	position: fixed;
	right: 30px;
	bottom: 150px;
	z-index: 10001;
	display: flex;
	flex-direction: column;
	align-items: center;

	.btn-inner {
		width: 64px;
		height: 64px;
		background: #0d68ff;
		box-shadow: 0 4px 15px rgba(13, 104, 255, 0.4);
		border-radius: 50%;
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		color: #fff;
		cursor: pointer;
		transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);

		&:hover {
			transform: scale(1.1);
			background: #3381ff;
		}

		i {
			font-size: 24px;
			margin-bottom: 2px;
		}

		span {
			font-size: 12px;
			font-weight: bold;
		}
	}

	.close-mode {
		position: absolute;
		top: -8px;
		right: -8px;
		width: 22px;
		height: 22px;
		background: #fff;
		border: 1px solid #eee;
		border-radius: 50%;
		display: flex;
		align-items: center;
		justify-content: center;
		cursor: pointer;
		box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
		transition: all 0.2s;

		&:hover {
			background: #f56c6c;
			color: #fff;
			border-color: #f56c6c;
		}

		i {
			font-size: 12px;
		}
	}
}

.screenshot-popover {
	padding: 5px 0 !important;

	.screenshot-menu {
		display: flex;
		flex-direction: column;

		.menu-item {
			padding: 12px 20px;
			font-size: 14px;
			color: #333;
			cursor: pointer;
			display: flex;
			align-items: center;
			transition: background 0.2s;

			i {
				font-size: 16px;
				margin-right: 10px;
				width: 16px;
				text-align: center;
			}

			&:hover {
				background: #f2f6ff;
				color: #0d68ff;
			}

			&.text-danger:hover {
				background: #fef0f0;
				color: #f56c6c;
			}
		}
	}
}
</style>